import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@sb/design-system';

import { ToolRunTicker } from './ToolRunTicker';
import type { ServerToolItem } from './types';

import tickerStyles from './ToolRunTicker.module.css';

interface ServerToolGroupProps {
  /** Initial tool sequence — usually all `'pending'`. */
  tools: ServerToolItem[];
  /**
   * Per-step delay in ms between status transitions. Item N becomes
   * running, then `delays[N]` ms later becomes complete. Defaults to
   * 300ms per step when not provided.
   */
  delays?: number[];
  /**
   * When true, the group is fully driven by the `tools` prop — no
   * internal animation timers and no auto-collapse. State syncs to
   * the latest `tools` array on every prop change so live tool_use /
   * tool_result events from the orchestrator can flip rows from
   * 'running' to 'complete' / 'error' in place. Default false
   * preserves the legacy uncontrolled / animated behavior used by
   * mock fixtures.
   */
  controlled?: boolean;
}

export const DEFAULT_STEP_DELAY_MS = 300;

const ICON_COLOR = 'var(--label-tertiary)';

function StatusIcon({ status }: { status: ServerToolItem['status'] }) {
  switch (status) {
    case 'complete':
      return (
        <Icon
          kind="checkmark"
          className="tw-shrink-0"
          // 14×14 isn't in the icon-size scale; muted icon color stays inline.
          style={{ width: 14, height: 14, color: ICON_COLOR }}
        />
      );
    case 'running':
      // No spinner glyph — a running row keeps the pending ring at
      // full opacity and its LABEL shimmers (see the row renderer),
      // matching the collapsed ticker's icon-less working treatment.
      return (
        <div
          className="tw-shrink-0"
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: `1.5px solid ${ICON_COLOR}`,
          }}
        />
      );
    case 'error':
      // Reuse the design-system X to stay neutral — error status keeps
      // the same muted color, the glyph alone signals "didn't finish".
      return (
        <Icon
          kind="xmark"
          className="tw-shrink-0"
          style={{ width: 14, height: 14, color: ICON_COLOR }}
        />
      );
    case 'pending':
    default:
      return (
        <div
          className="tw-shrink-0"
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: `1.5px solid ${ICON_COLOR}`,
            opacity: 0.5,
          }}
        />
      );
  }
}

/**
 * Tool-run summary. Renders as a SINGLE ticking line — `⟳ {x} tools
 * ran` — whose count bumps up as each tool starts, instead of the old
 * line-per-tool stream (which got spammy on tool-heavy turns). The
 * line is clickable to expand into the full per-tool list (muted icon
 * + label per row, Claude Code style), and collapses back down.
 *
 * All icons render in the same muted color so the row stays
 * visually quiet against the chat background; status is conveyed
 * by the icon SHAPE (spinner / checkmark / x), not by color.
 */
export function ServerToolGroup({
  tools: initialTools,
  delays = [],
  controlled = false,
}: ServerToolGroupProps) {
  const [tools, setTools] = useState<ServerToolItem[]>(initialTools);
  const [isExpanded, setIsExpanded] = useState(false);

  // In controlled mode the parent owns the status — re-sync local
  // state whenever the prop changes so live tool_result events flip
  // the row in place. Skipped in uncontrolled mode so the existing
  // mock-driven animation isn't clobbered.
  useEffect(() => {
    if (!controlled) return;
    setTools(initialTools);
  }, [controlled, initialTools]);

  // A failed tool auto-expands the group so the failure stays visible
  // instead of hiding behind the one-line summary.
  const hasError = tools.some((t) => t.status === 'error');

  useEffect(() => {
    if (hasError) setIsExpanded(true);
  }, [hasError]);

  // Every timeout we schedule lands here so the cleanup effect can
  // clear them on unmount. Without this, a card that scrolls out of
  // the panel mid-animation will keep firing setState on a dead
  // component.
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Schedule the running → complete transitions once on mount.
  // `initialTools` and `delays` are snapshot-at-mount by design — the
  // component does not respond to prop changes after the first render.
  // The empty dep array is intentional: a parent re-render that passes
  // new array literals must NOT tear down the in-flight timeouts
  // (which `[initialTools, delays]` deps did, freezing the animation
  // mid-sequence).
  //
  // Cleanup clears the scheduled timeouts AND resets the ref so React
  // 18 Strict Mode's dev-only unmount-then-remount cycle replays the
  // animation cleanly — without the reset, the second mount would
  // re-use the cleared timeout array and the animation would stall
  // with every row stuck in `pending`.
  useEffect(() => {
    if (controlled) return undefined;

    let cumulativeDelay = 0;

    initialTools.forEach((_tool, index) => {
      const stepDelay = delays[index] ?? DEFAULT_STEP_DELAY_MS;

      timeoutsRef.current.push(
        setTimeout(() => {
          setTools((prev) =>
            prev.map((t, i) => (i === index ? { ...t, status: 'running' } : t)),
          );
        }, cumulativeDelay),
      );

      cumulativeDelay += stepDelay;

      timeoutsRef.current.push(
        setTimeout(() => {
          setTools((prev) =>
            prev.map((t, i) =>
              i === index ? { ...t, status: 'complete' } : t,
            ),
          );
        }, cumulativeDelay),
      );
    });

    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only; see comment above
  }, []);

  // The ticker counts tools that have STARTED (running or finished) so
  // the number bumps the moment the agent calls another tool, not when
  // the result lands.
  const startedCount = tools.filter((t) => t.status !== 'pending').length;

  const allDone =
    tools.length > 0 &&
    tools.every((t) => t.status === 'complete' || t.status === 'error');

  if (!isExpanded) {
    return (
      <ToolRunTicker
        count={startedCount}
        isDone={allDone}
        hasError={hasError}
        onExpand={() => setIsExpanded(true)}
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <button
        type="button"
        onClick={() => setIsExpanded(false)}
        className={cx(
          'tw-inline-flex',
          'tw-items-center',
          'tw-bg-transparent',
          'tw-border-none',
          'tw-p-0',
          'tw-cursor-pointer',
          'tw-text-left',
        )}
        style={{
          // 12px font + 30%-black collapse-link color + inherited
          // font family don't have tokens; bottom margin 2px stays
          // inline (the spacing scale starts at 2 but `tw-mb-2`
          // refers to 2px — leave inline for the asymmetric `0 0 2 0`).
          margin: '0 0 2px 0',
          fontFamily: 'inherit',
          fontSize: 12,
          color: 'var(--label-quaternary)',
        }}
      >
        ▾ Collapse
      </button>
      {tools.map((tool) => (
        <div
          key={tool.id}
          className="tw-flex tw-items-center tw-gap-8 tw-text-13"
          style={{
            color: 'var(--label-secondary)',
          }}
        >
          <StatusIcon status={tool.status} />
          <span
            className={cx(
              tool.status === 'running' && tickerStyles.shimmerText,
            )}
          >
            {tool.label}
          </span>
        </div>
      ))}
    </div>
  );
}
